import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { PromotionForm } from '../../components/admin/PromotionForm';
import { PromotionList } from '../../components/admin/PromotionList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPromotions = () => {
  const { isStaff, hasPermission } = useAuth();
  const navigate = useNavigate();
  const canAccess = isStaff && hasPermission('promo');
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | promo object
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/promotions`);
      setPromos(res.data);
    } catch {
      toast.error('Impossible de charger les promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canAccess) { navigate('/connexion'); return; }
    load();
  }, [canAccess, navigate, load]);

  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/promotions/${id}`);
      setEditing(res.data);
    } catch { toast.error('Promotion introuvable'); }
  };

  const save = async (payload, id) => {
    if (id) await axios.put(`${API_URL}/api/admin/promotions/${id}`, payload);
    else await axios.post(`${API_URL}/api/admin/promotions`, payload);
    toast.success(id ? 'Promotion mise à jour' : 'Promotion créée');
    setEditing(null);
    load();
  };

  const toggleActive = async (p) => {
    await axios.put(`${API_URL}/api/admin/promotions/${p.id}`, { active: !p.active }).catch(() => toast.error('Échec'));
    load();
  };

  const relaunch = async (p) => {
    if (!window.confirm(`Relancer « ${p.name} » pour 14 jours à partir d'aujourd'hui ?`)) return;
    await axios.post(`${API_URL}/api/admin/promotions/${p.id}/relaunch?days=14`).catch(() => toast.error('Échec'));
    toast.success('Promotion relancée');
    load();
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la promotion « ${p.name} » ?`)) return;
    await axios.delete(`${API_URL}/api/admin/promotions/${p.id}`).catch(() => toast.error('Échec'));
    load();
  };

  if (!canAccess) return null;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] pt-24 pb-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm mb-2 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Tableau de bord
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-red-400 text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Tag className="w-4 h-4" /> Déstockage</p>
            <h1 className="text-3xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Promotions</h1>
            <p className="text-zinc-500 text-sm mt-1">Nouveaux prix sur une sélection de parfums, pendant une période limitée. Les clients voient la date de fin.</p>
          </div>
          {!editing && (
            <button type="button" onClick={() => setEditing('new')}
              className="inline-flex items-center gap-2 bg-[color:var(--color-primary)] text-black px-4 py-2 text-sm hover:bg-[color:var(--color-primary-hover)]" data-testid="promo-new-btn">
              <Plus className="w-4 h-4" /> Nouvelle promotion
            </button>
          )}
        </div>

        {editing ? (
          <PromotionForm promo={editing === 'new' ? null : editing} onCancel={() => setEditing(null)} onSave={save} />
        ) : loading ? (
          <p className="text-zinc-500 text-sm">Chargement...</p>
        ) : (
          <PromotionList promos={promos} onEdit={openEdit} onToggle={toggleActive} onRelaunch={relaunch} onDelete={remove} />
        )}
      </div>
    </div>
  );
};

export default AdminPromotions;
